import { useCallback, useRef } from 'react'

export type MessageHandler = (data: unknown) => void
export type MessageHandlerRegistry = Map<string, Set<MessageHandler>>

export const CUSTOM_MESSAGE_TYPES = new Set(['analyze_result'])

export function isCustomHandlerType(object: unknown): object is { type: string } {
    return typeof object === 'object' && object !== null && 'type' in object && typeof object.type === 'string' && CUSTOM_MESSAGE_TYPES.has(object.type)
}

export function useWebSocketHandlers() {
    const handlersRef = useRef<MessageHandlerRegistry>(new Map())

    const registerMessageHandler = useCallback((type: string, handler: MessageHandler) => {
        if (!handlersRef.current.has(type)) {
            handlersRef.current.set(type, new Set())
        }
        handlersRef.current.get(type)!.add(handler)

        return () => {
            handlersRef.current.get(type)?.delete(handler)
            if (handlersRef.current.get(type)?.size === 0) {
                handlersRef.current.delete(type)
            }
        }
    }, [])

    const handleCustomMessage = useCallback((message: unknown) => {
        const messageType = (message as { type?: string }).type
        if (!messageType) return false

        const typeHandlers = handlersRef.current.get(messageType)
        if (typeHandlers && typeHandlers.size > 0) {
            typeHandlers.forEach(h => h(message))
            return true
        }

        const wildcard = handlersRef.current.get('*')
        if (wildcard && wildcard.size > 0) {
            wildcard.forEach(h => h(message))
            return true
        }

        return false
    }, [])

    return { registerMessageHandler, handleCustomMessage, handlersRef }
}
